import React, { useState, useEffect } from 'react';
import Footer from '../components/Footer';
import axiosInstance from '../utils/axios';
import {
  Table, Button, Input, Modal, Form, Select, message,
  Card, Space, Tag, Tooltip, Popconfirm,
} from 'antd';
import {
  UserOutlined, PlusOutlined, SearchOutlined, HomeOutlined, TeamOutlined,
  CarOutlined, LockOutlined, EditOutlined, DeleteOutlined, EyeOutlined, EyeInvisibleOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { hssColors, commonStyles } from '../config/theme';
import { isAdmin } from '../utils/auth';

const { Option } = Select;

const UsuariosClientes = () => {
  const navigate = useNavigate();
  const [registros, setRegistros] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [vehiculosCliente, setVehiculosCliente] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalEditarVisible, setModalEditarVisible] = useState(false);
  const [editando, setEditando] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [mostrarPass, setMostrarPass] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(true);
  const [form] = Form.useForm();
  const [formEditar] = Form.useForm();

  useEffect(() => {
    cargar();
    axiosInstance.get('/clientes').then(r => setClientes(r.data)).catch(() => {});
  }, []);

  const cargar = async () => {
    setLoading(true);
    try {
      const r = await axiosInstance.get('/usuarios-clientes');
      setRegistros(r.data);
    } catch {
      message.error('Error al cargar registros');
    } finally {
      setLoading(false);
    }
  };

  const onClienteChange = async (dni) => {
    const cliente = clientes.find(c => c.dni === dni);
    if (!cliente) return;
    try {
      const r = await axiosInstance.get(`/clientes/${dni}`);
      setVehiculosCliente(r.data.vehiculos || []);
    } catch {
      setVehiculosCliente([]);
    }
    form.setFieldValue('vehiculos_chapas', []);
  };

  const onClienteChangeEditar = async (dni) => {
    try {
      const r = await axiosInstance.get(`/clientes/${dni}`);
      setVehiculosCliente(r.data.vehiculos || []);
    } catch {
      setVehiculosCliente([]);
    }
    formEditar.setFieldValue('vehiculos_chapas', []);
  };

  const onFinish = async (values) => {
    try {
      await axiosInstance.post('/usuarios-clientes', values);
      message.success('Usuario registrado');
      setModalVisible(false);
      form.resetFields();
      setVehiculosCliente([]);
      cargar();
    } catch (e) {
      message.error(e.response?.data?.error || 'Error al guardar');
    }
  };

  const abrirEditar = async (r) => {
    setEditando(r);
    try {
      const res = await axiosInstance.get(`/clientes/${r.cliente_dni}`);
      setVehiculosCliente(res.data.vehiculos || []);
    } catch { setVehiculosCliente([]); }
    formEditar.setFieldsValue({
      usuario: r.usuario,
      contrasena: r.contrasena,
      observaciones: r.observaciones,
      cliente_dni: r.cliente_dni,
      vehiculos_chapas: r.vehiculos.map(v => v.chapa),
    });
    setModalEditarVisible(true);
  };

  const onFinishEditar = async (values) => {
    try {
      await axiosInstance.put(`/usuarios-clientes/${editando.id}`, values);
      message.success('Actualizado');
      setModalEditarVisible(false);
      cargar();
    } catch (e) {
      message.error(e.response?.data?.error || 'Error al actualizar');
    }
  };

  const eliminar = async (id) => {
    try {
      await axiosInstance.delete(`/usuarios-clientes/${id}`);
      message.success('Eliminado');
      cargar();
    } catch {
      message.error('Error al eliminar');
    }
  };

  const filtrados = registros.filter(r => {
    if (!searchText) return true;
    const s = searchText.toLowerCase();
    return (
      r.usuario?.toLowerCase().includes(s) ||
      r.cliente_nombre?.toLowerCase().includes(s) ||
      r.vehiculos?.some(v => v.chapa?.toLowerCase().includes(s))
    );
  });

  // Resetear a página 1 cuando cambia el filtro
  useEffect(() => { setCurrentPage(1); }, [searchText]);

  const columns = [
    {
      title: 'Usuario',
      dataIndex: 'usuario',
      key: 'usuario',
      sorter: (a, b) => (a.usuario || '').localeCompare(b.usuario || ''),
      render: u => (
        <span style={{
          padding: '3px 10px',
          background: hssColors.gray100,
          borderRadius: 6,
          fontFamily: 'monospace',
          fontWeight: 600,
          color: hssColors.primary,
        }}>{u}</span>
      ),
    },
    {
      title: 'Contraseña',
      dataIndex: 'contrasena',
      key: 'contrasena',
      render: (pass, r) => (
        <Space>
          <span style={{ fontFamily: 'monospace' }}>
            {mostrarPass[r.id] ? pass : '••••••••'}
          </span>
          <Tooltip title={mostrarPass[r.id] ? 'Ocultar' : 'Mostrar'}>
            <Button
              type="text" size="small"
              icon={mostrarPass[r.id] ? <EyeInvisibleOutlined /> : <EyeOutlined />}
              onClick={() => setMostrarPass(p => ({ ...p, [r.id]: !p[r.id] }))}
            />
          </Tooltip>
        </Space>
      ),
    },
    {
      title: 'Cliente',
      dataIndex: 'cliente_nombre',
      key: 'cliente_nombre',
      sorter: (a, b) => (a.cliente_nombre || '').localeCompare(b.cliente_nombre || ''),
      render: (nombre, r) => (
        <Space>
          <UserOutlined style={{ color: hssColors.primary }} />
          <span
            style={{ cursor: 'pointer', color: hssColors.primary, textDecoration: 'underline' }}
            onClick={() => navigate(`/clientes/${r.cliente_dni}`)}
          >
            {nombre}
          </span>
        </Space>
      ),
    },
    {
      title: 'Vehículos vinculados',
      dataIndex: 'vehiculos',
      key: 'vehiculos',
      render: vehiculos => (
        <Space wrap>
          {vehiculos?.length > 0
            ? vehiculos.map(v => (
                <Tag
                  key={v.chapa}
                  icon={<CarOutlined />}
                  color="blue"
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/vehiculos/${v.chapa}`)}
                >
                  {v.chapa} {v.marca} {v.modelo}
                </Tag>
              ))
            : <span style={{ color: hssColors.gray400 }}>Sin vehículos</span>
          }
        </Space>
      ),
    },
    {
      title: 'Observaciones',
      dataIndex: 'observaciones',
      key: 'observaciones',
      render: obs => obs || <span style={{ color: hssColors.gray400 }}>—</span>,
    },
    ...(isAdmin() ? [{
      title: '',
      key: 'acciones',
      width: 90,
      render: (_, r) => (
        <Space>
          <Tooltip title="Editar">
            <Button type="text" icon={<EditOutlined />} onClick={() => abrirEditar(r)} style={{ color: hssColors.primary }} />
          </Tooltip>
          <Popconfirm title="¿Eliminar este registro?" onConfirm={() => eliminar(r.id)} okText="Sí" cancelText="No">
            <Tooltip title="Eliminar">
              <Button type="text" icon={<DeleteOutlined />} danger />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    }] : []),
  ];

  const FormVehiculos = ({ vehículos, form: f }) => (
    <Form.Item name="vehiculos_chapas" label="Vehículos vinculados">
      <Select
        mode="multiple"
        placeholder="Seleccioná los vehículos"
        size="large"
        style={{ borderRadius: 8 }}
        optionFilterProp="label"
        notFoundContent={vehiculosCliente.length === 0 ? 'Seleccioná un cliente primero' : 'Sin vehículos'}
      >
        {vehiculosCliente.map(v => (
          <Option key={v.chapa} value={v.chapa} label={`${v.chapa} — ${v.marca} ${v.modelo} ${v.servicio ? `(${v.servicio})` : ''}`}>
            {v.chapa} — {v.marca} {v.modelo} {v.servicio ? `(${v.servicio})` : ''}
          </Option>
        ))}
      </Select>
    </Form.Item>
  );

  return (
    <div style={{ minHeight: '100vh', background: hssColors.backgroundPrimary, display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ background: hssColors.backgroundSecondary, borderBottom: `1px solid ${hssColors.gray200}`, padding: '16px 32px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <TeamOutlined style={{ fontSize: 28, color: hssColors.primary }} />
            <div>
              <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: hssColors.primary }}>Users / Pass</h1>
              <p style={{ margin: 0, fontSize: 13, color: hssColors.textSecondary }}>Registro de accesos al sistema de rastreo satelital</p>
            </div>
          </div>
          <Space>
            <Button icon={<HomeOutlined />} onClick={() => navigate('/dashboard')} size="large" style={{ height: 44, borderRadius: 8 }}>Inicio</Button>
            {isAdmin() && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalVisible(true)} size="large" style={{ height: 44, borderRadius: 8, fontWeight: 600 }}>
                Nuevo Registro
              </Button>
            )}
          </Space>
        </div>
      </div>

      {/* Contenido */}
      <div style={{ padding: '24px 32px', flex: 1 }}>
        {/* Stats */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
          {[
            { label: 'Total usuarios', value: registros.length, color: hssColors.primary, icon: <UserOutlined /> },
            { label: 'Clientes con acceso', value: new Set(registros.map(r => r.cliente_dni)).size, color: hssColors.success, icon: <UserOutlined /> },
            { label: 'Vehículos vinculados', value: registros.reduce((acc, r) => acc + (r.vehiculos?.length || 0), 0), color: '#e67e22', icon: <CarOutlined /> },
          ].map(s => (
            <Card key={s.label} loading={loading} style={{ flex: 1, borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ fontSize: 28, color: s.color }}>{s.icon}</div>
                <div>
                  <div style={{ fontSize: 28, fontWeight: 700, color: s.color, lineHeight: 1 }}>{s.value}</div>
                  <div style={{ fontSize: 12, color: hssColors.textSecondary }}>{s.label}</div>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Búsqueda */}
        <Card style={{ marginBottom: 24, borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd }} styles={{ body: { padding: '20px 24px' } }}>
          <Input
            placeholder="Buscar por usuario, cliente o chapa..."
            prefix={<SearchOutlined style={{ color: hssColors.gray400 }} />}
            style={{ width: '100%', borderRadius: 8 }}
            size="large"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            allowClear
          />
        </Card>

        {/* Tabla */}
        <Card style={{ borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd }} styles={{ body: { padding: 0 } }}>
          <Table
            columns={columns}
            dataSource={filtrados}
            rowKey="id"
            scroll={{ x: 'max-content' }}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: filtrados.length,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50'],
              showTotal: t => `Total: ${t} registros`,
              onChange: (page, size) => { setCurrentPage(page); setPageSize(size); },
            }}
            style={{ borderRadius: 12, overflow: 'hidden' }}
          />
        </Card>
      </div>

      {/* Modal Nuevo */}
      <Modal
        title={<div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 20, fontWeight: 700, color: hssColors.primary }}><UserOutlined /><span>Nuevo Usuario de Cliente</span></div>}
        open={modalVisible}
        onCancel={() => { setModalVisible(false); form.resetFields(); setVehiculosCliente([]); }}
        footer={null}
        width={620}
      >
        <Form form={form} onFinish={onFinish} layout="vertical" style={{ marginTop: 24 }}>
          <Form.Item name="cliente_dni" label="Cliente (opcional)">
            <Select
              showSearch size="large" placeholder="Buscar cliente..."
              optionFilterProp="label"
              onChange={onClienteChange}
              style={{ borderRadius: 8 }}
            >
              {clientes.map(c => (
                <Option key={c.dni} value={c.dni} label={`${c.nombre} ${c.apellido} DNI ${c.dni}`}>{c.nombre} {c.apellido} (DNI {c.dni})</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="usuario" label="Usuario" rules={[{ required: true, message: 'Ingresá el usuario' }]}>
            <Input prefix={<UserOutlined style={{ color: hssColors.gray400 }} />} placeholder="Usuario de acceso" size="large" style={{ borderRadius: 8 }} />
          </Form.Item>

          <Form.Item name="contrasena" label="Contraseña" rules={[{ required: true, message: 'Ingresá la contraseña' }]}>
            <Input prefix={<LockOutlined style={{ color: hssColors.gray400 }} />} placeholder="Contraseña" size="large" style={{ borderRadius: 8 }} />
          </Form.Item>

          <FormVehiculos vehiculosCliente={vehiculosCliente} form={form} />

          <Form.Item name="observaciones" label="Observaciones">
            <Input placeholder="Notas adicionales (opcional)" size="large" style={{ borderRadius: 8 }} />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => { setModalVisible(false); form.resetFields(); setVehiculosCliente([]); }} size="large" style={{ borderRadius: 8 }}>Cancelar</Button>
              <Button type="primary" htmlType="submit" size="large" style={{ borderRadius: 8, fontWeight: 600 }}>Guardar</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal Editar */}
      <Modal
        title={<div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 20, fontWeight: 700, color: hssColors.primary }}><EditOutlined /><span>Editar Usuario</span></div>}
        open={modalEditarVisible}
        onCancel={() => { setModalEditarVisible(false); setVehiculosCliente([]); }}
        footer={null}
        width={620}
      >
        <Form form={formEditar} onFinish={onFinishEditar} layout="vertical" style={{ marginTop: 24 }}>
          <Form.Item name="cliente_dni" label="Cliente">
            <Select showSearch size="large" optionFilterProp="label" onChange={onClienteChangeEditar} style={{ borderRadius: 8 }}>
              {clientes.map(c => (
                <Option key={c.dni} value={c.dni} label={`${c.nombre} ${c.apellido} DNI ${c.dni}`}>{c.nombre} {c.apellido} (DNI {c.dni})</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="usuario" label="Usuario" rules={[{ required: true }]}>
            <Input prefix={<UserOutlined style={{ color: hssColors.gray400 }} />} size="large" style={{ borderRadius: 8 }} />
          </Form.Item>

          <Form.Item name="contrasena" label="Contraseña" rules={[{ required: true }]}>
            <Input prefix={<LockOutlined style={{ color: hssColors.gray400 }} />} size="large" style={{ borderRadius: 8 }} />
          </Form.Item>

          <Form.Item name="vehiculos_chapas" label="Vehículos vinculados">
            <Select mode="multiple" size="large" style={{ borderRadius: 8 }} optionFilterProp="label"
              notFoundContent={vehiculosCliente.length === 0 ? 'Seleccioná un cliente primero' : 'Sin vehículos'}>
              {vehiculosCliente.map(v => (
                <Option key={v.chapa} value={v.chapa} label={`${v.chapa} — ${v.marca} ${v.modelo} ${v.servicio ? `(${v.servicio})` : ''}`}>
                  {v.chapa} — {v.marca} {v.modelo} {v.servicio ? `(${v.servicio})` : ''}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="observaciones" label="Observaciones">
            <Input size="large" style={{ borderRadius: 8 }} />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => setModalEditarVisible(false)} size="large" style={{ borderRadius: 8 }}>Cancelar</Button>
              <Button type="primary" htmlType="submit" size="large" style={{ borderRadius: 8, fontWeight: 600 }}>Guardar cambios</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Footer />
    </div>
  );
};

export default UsuariosClientes;
